import React, { useState, useEffect } from "react";
import { News } from "@/types";
import LazyImageArticleCard from "@/components/articles/LazyImageArticleCard";
import { NewsErrorBoundary } from "@/components/ui/ErrorBoundary";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { is } from "date-fns/locale";

interface SimpleNewsCarouselProps {
  news: News[];
  title?: string;
  className?: string;
  itemsPerView?: number;
  autoPlay?: boolean;
  autoPlayDelay?: number;
  showArrows?: boolean;
  variant?: "compact" | "row" | "hero" | "sidebar";
  loop?: boolean;
}

const SimpleNewsCarousel: React.FC<SimpleNewsCarouselProps> = ({
  news,
  title,
  className = "",
  itemsPerView = 3,
  autoPlay = false,
  autoPlayDelay = 5000,
  showArrows = true,
  variant = "compact",
  loop = true,
}) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Auto-play functionality
  useEffect(() => {
    if (!api || !autoPlay || isHovered) return;

    const interval = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext();
      } else if (loop) {
        api.scrollTo(0);
      }
    }, autoPlayDelay);

    return () => clearInterval(interval);
  }, [api, autoPlay, autoPlayDelay, isHovered, loop]);

  // Update carousel state
  useEffect(() => {
    if (!api) return;

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  if (!news || news.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        {title && (
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {title}
            </h2>
          </div>
        )}
        <div className="text-gray-500 dark:text-gray-400">
          Tidak ada berita tersedia
        </div>
      </div>
    );
  }

  // Calculate responsive basis class - mobile: 1 item, desktop: itemsPerView
  const getBasisClass = () => {
    // Mobile-first approach:
    // xs-sm: 1 item (basis-full)
    // md-lg: itemsPerView items
    if (itemsPerView === 1) {
      return "basis-full";
    } else if (itemsPerView === 2) {
      return "basis-full md:basis-1/2";
    } else if (itemsPerView === 3) {
      return "basis-full md:basis-1/3";
    } else if (itemsPerView === 4) {
      return "basis-full sm:basis-1/2 md:basis-1/4";
    } else {
      // Default for any other value - mobile 1, desktop 3
      return "basis-full md:basis-1/3";
    }
  };

  return (
    <div
      className={className}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Title */}
      {title && (
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {title}
          </h2>
        </div>
      )}

      {/* Carousel Container */}
      <Carousel
        setApi={setApi}
        className="w-full"
        opts={{
          align: "start",
          loop: loop,
          slidesToScroll: 1,
          containScroll: "trimSnaps",
          breakpoints: {
            "(max-width: 768px)": {
              slidesToScroll: 1,
            },
            "(min-width: 768px)": {
              slidesToScroll: itemsPerView > 1 ? Math.min(itemsPerView, 3) : 1,
            },
          },
        }}
      >
        <CarouselContent className="-ml-1 md:-ml-4">
          {news.map((newsItem, index) => (
            <CarouselItem
              key={`carousel-item-${newsItem.id}-${index}`}
              className={`pl-1 md:pl-4 ${getBasisClass()}`}
            >
              <div className="p-1 cursor-pointer">
                <NewsErrorBoundary
                  key={`carousel-boundary-${newsItem.id}`}
                  title="Carousel Article"
                >
                  <LazyImageArticleCard
                    news={newsItem}
                    variant={"hero"}
                    showViews={false}
                    showReactions={false}
                    prefetchStrategy="viewport"
                    titleClassName="text-lg"
                    className="h-full p-2 lg:p-3 border border-gray-400/20 duration-200 rounded-xl overflow-hidden"
                  />
                </NewsErrorBoundary>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>

        {/* Navigation Arrows */}
        {showArrows && (
          <>
            <CarouselPrevious
              className={`-left-2 lg:-left-5 h-9 w-9 lg:h-12 lg:w-12 rounded-full
                  bg-white/10 dark:bg-gray-900/20
                  backdrop-blur-md border border-white/30 dark:border-gray-700/50
                  shadow-lg transition ease-in-out
                  hover:bg-white/40 dark:hover:bg-gray-900/40 hover:scale-110 hover:border-gray-700/20 dark:hover:border-white/20
                  ${isHovered ? "opacity-100" : "opacity-0"} `}
            />
            <CarouselNext
              className={`-right-2 lg:-right-5 h-9 w-9 lg:h-12 lg:w-12 rounded-full
                  bg-white/10 dark:bg-gray-900/20
                  backdrop-blur-md border border-white/30 dark:border-gray-700/50
                  shadow-lg transition ease-in-out
                  hover:bg-white/40 dark:hover:bg-gray-900/40 hover:scale-110 hover:border-gray-700/20 dark:hover:border-white/20
                  ${isHovered ? "opacity-100" : "opacity-0"} `}
            />
          </>
        )}

        {/* Auto-play indicator */}
        {autoPlay && (
          <div
            className={`absolute -top-8 right-2 z-20 bg-black/5 text-[#de0b66] back text-[10px] font-bold px-2 py-1 rounded backdrop-filter backdrop-blur-lg transition-all ease-in-out cursor-default ${
              !isHovered ? "opacity-0 translate-y-6" : "opacity-100"
            }`}
          >
            Auto-play paused
          </div>
        )}
      </Carousel>

      {/* Progress indicator */}
      {autoPlay && !isHovered && (
        <div
          className={`mt-1 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1 overflow-hidden`}
        >
          <div
            className="bg-[#de0b66] h-1 rounded-full carousel-progress"
            style={{
              animation: `progressBar ${autoPlayDelay / 1000}s linear infinite`,
            }}
          />
        </div>
      )}

      {/* Debug info */}
      {/*<div className="mt-2 text-xs text-gray-500">
        Items per view: {itemsPerView} | Slide {current} of {count} | Total
        news: {news.length}
      </div>*/}

      {/* Slide counter for mobile */}
      <div className="flex justify-center mt-4 sm:hidden">
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {current} / {count}
        </div>
      </div>

      {/* Touch indicator for mobile */}
      <div className="block sm:hidden text-center mt-2">
        <p className="text-xs text-gray-400 dark:text-gray-500">
          ← Geser untuk navigasi →
        </p>
      </div>

      {/* Screen Reader Information */}
      <div className="sr-only" role="status" aria-live="polite">
        Slide {current} of {count}
      </div>
    </div>
  );
};

export default SimpleNewsCarousel;
